import React from 'react';
import styled from '@emotion/styled';
import { color_colors_ocean, color_colors_decline, color_colors_growth, color_colors_gold } from '../../../../constants/colors';

// Shared building blocks for the organization Management tab.
// Modeled after Apple's grouped settings lists: a soft gray canvas, rounded white groups,
// hairline separators, pill buttons and a segmented control for switching tools.

export const ui = {
    canvas: '#F5F5F7',
    card: '#FFFFFF',
    label: '#1D1D1F',
    secondaryLabel: '#6E6E73',
    tertiaryLabel: '#AEAEB2',
    separator: 'rgba(60, 60, 67, 0.12)',
    fill: 'rgba(118, 118, 128, 0.12)',
    fillHover: 'rgba(118, 118, 128, 0.2)',
    inputFill: 'rgba(118, 118, 128, 0.08)',
    accent: color_colors_ocean,
    accentHover: '#18689A',
    accentSoft: 'rgba(30, 126, 185, 0.1)',
    accentRing: 'rgba(30, 126, 185, 0.25)',
    destructive: color_colors_decline,
    success: color_colors_growth,
    warning: color_colors_gold,
    radius: '12px',
    monospace: `'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace`
};

/* ---------- Segmented control ---------- */

export interface SegmentOption {
    id: string;
    label: string;
}

interface SegmentedControlProps {
    options: SegmentOption[];
    value: string;
    onChange: (id: string) => void;
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, value, onChange }) => (
    <SegmentTrack role="tablist">
        {options.map(option => (
            <Segment
                key={option.id}
                type="button"
                role="tab"
                aria-selected={option.id === value}
                active={option.id === value}
                onClick={() => onChange(option.id)}
            >
                {option.label}
            </Segment>
        ))}
    </SegmentTrack>
);

const SegmentTrack = styled.div`
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
    overflow-x: auto;
    padding: 2px;
    gap: 2px;
    border-radius: 9px;
    background: ${ui.fill};

    /* On phones, wrap instead of hiding tools off-screen */
    @media (max-width: 720px) {
        flex-wrap: wrap;
        overflow-x: visible;
    }
`;

const Segment = styled.button<{ active: boolean }>`
    flex-shrink: 0;
    border: 0;
    margin: 0;
    padding: 6px 16px;
    border-radius: 7px;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${props => props.active ? 600 : 500};
    color: ${ui.label};
    white-space: nowrap;
    cursor: pointer;
    background: ${props => props.active ? ui.card : 'transparent'};
    box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.04)' : 'none'};
    transition: background 0.15s ease;

    &:hover {
        background: ${props => props.active ? ui.card : 'rgba(255, 255, 255, 0.5)'};
    }

    &:focus-visible {
        outline: 2px solid ${ui.accentRing};
    }
`;

/* ---------- Buttons ---------- */

export type ActionButtonVariant = 'primary' | 'secondary' | 'destructive' | 'plain';

interface ActionButtonProps {
    text: string;
    onClick?: () => void;
    variant?: ActionButtonVariant;
    disabled?: boolean;
    title?: string;
}

export const ActionButton: React.FC<ActionButtonProps> = ({ text, onClick, variant = 'secondary', disabled, title }) => (
    <PillButton type="button" variant={variant} disabled={disabled} onClick={onClick} title={title}>
        {text}
    </PillButton>
);

const buttonColors: Record<ActionButtonVariant, { background: string; hover: string; color: string }> = {
    primary: { background: ui.accent, hover: ui.accentHover, color: '#FFFFFF' },
    secondary: { background: ui.fill, hover: ui.fillHover, color: ui.accent },
    destructive: { background: ui.fill, hover: ui.fillHover, color: ui.destructive },
    plain: { background: 'transparent', hover: ui.accentSoft, color: ui.accent }
};

const PillButton = styled.button<{ variant: ActionButtonVariant }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-height: ${props => props.variant === 'plain' ? '28px' : '32px'};
    padding: 0 ${props => props.variant === 'plain' ? '8px' : '16px'};
    border: 0;
    border-radius: 980px;
    font-family: inherit;
    font-size: 13px;
    font-weight: ${props => props.variant === 'primary' ? 600 : 500};
    white-space: nowrap;
    cursor: pointer;
    color: ${props => buttonColors[props.variant].color};
    background: ${props => buttonColors[props.variant].background};
    transition: background 0.15s ease, opacity 0.15s ease;

    &:hover:not(:disabled) {
        background: ${props => buttonColors[props.variant].hover};
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${ui.accentRing};
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

/* ---------- Groups ---------- */

export const GroupSection = styled.section`
    display: flex;
    flex-direction: column;
`;

export const GroupTitle = styled.h3`
    margin: 0 0 8px 16px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${ui.secondaryLabel};
`;

export const GroupCaption = styled.p`
    margin: 8px 16px 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${ui.secondaryLabel};
`;

export const Group = styled.div`
    background: ${ui.card};
    border-radius: ${ui.radius};
    box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;

    & > * + * {
        border-top: 0.5px solid ${ui.separator};
    }
`;

export const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 44px;
    padding: 10px 16px;
    box-sizing: border-box;
`;

export const RowText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

export const RowTitle = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${ui.label};
`;

export const RowDetail = styled.span`
    font-size: 12px;
    line-height: 1.4;
    color: ${ui.secondaryLabel};
`;

export const GroupFooter = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(246, 246, 248, 0.6);
`;

export const FooterNote = styled.span`
    font-size: 12px;
    color: ${ui.secondaryLabel};
`;

export const ButtonRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

/* ---------- Action rows ---------- */

interface ActionRowProps {
    title: string;
    description?: string;
    actionText: string;
    onAction: () => void;
    disabled?: boolean;
    variant?: ActionButtonVariant;
}

export const ActionRow: React.FC<ActionRowProps> = ({ title, description, actionText, onAction, disabled, variant = 'secondary' }) => (
    <Row>
        <RowText>
            <RowTitle>{title}</RowTitle>
            {description && <RowDetail>{description}</RowDetail>}
        </RowText>
        <ActionButton text={actionText} onClick={onAction} disabled={disabled} variant={variant} />
    </Row>
);

/* ---------- Form fields ---------- */

interface FieldRowProps {
    label: string;
    htmlFor?: string;
    hint?: string;
}

export const FieldRow: React.FC<FieldRowProps> = ({ label, htmlFor, hint, children }) => (
    <FieldRowContainer>
        <FieldLabelColumn>
            <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
            {hint && <RowDetail>{hint}</RowDetail>}
        </FieldLabelColumn>
        <FieldControl>{children}</FieldControl>
    </FieldRowContainer>
);

const FieldRowContainer = styled(Row)`
    align-items: flex-start;
    padding-top: 12px;
    padding-bottom: 12px;

    @media (max-width: 720px) {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }
`;

const FieldLabelColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 200px;
    flex-shrink: 0;
    padding-top: 8px;

    @media (max-width: 720px) {
        width: auto;
        padding-top: 0;
    }
`;

const FieldLabel = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: ${ui.label};
`;

const FieldControl = styled.div`
    flex: 1;
    min-width: 0;

    /* Keep long dropdown values from running under the arrow */
    .Select-value {
        padding-right: 40px !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const inputStyles = `
    width: 100%;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 8px;
    background: ${ui.inputFill};
    font-family: inherit;
    font-size: 14px;
    color: ${ui.label};
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
        color: ${ui.tertiaryLabel};
    }

    &:focus {
        outline: none;
        background: ${ui.card};
        border-color: ${ui.accent};
        box-shadow: 0 0 0 3px ${ui.accentRing};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const TextField = styled.input`
    ${inputStyles}
    height: 36px;
    padding: 0 12px;
`;

export const TextArea = styled.textarea<{ monospace?: boolean }>`
    ${inputStyles}
    padding: 8px 12px;
    line-height: 1.5;
    resize: vertical;
    font-family: ${props => props.monospace ? ui.monospace : 'inherit'};
    font-size: ${props => props.monospace ? '13px' : '14px'};
`;

/* ---------- Selection check ---------- */

// Circular checkmark used for selectable rows (like selecting items in Photos or Mail)
export const SelectionCheck = styled.input`
    appearance: none;
    -webkit-appearance: none;
    flex-shrink: 0;
    display: grid;
    place-content: center;
    width: 20px;
    height: 20px;
    margin: 0;
    border: 1.5px solid #C7C7CC;
    border-radius: 50%;
    background: ${ui.card};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &::after {
        content: '';
        width: 5px;
        height: 9px;
        margin-top: -2px;
        border: solid #FFFFFF;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg) scale(0);
        transition: transform 0.15s ease;
    }

    &:checked {
        background: ${ui.accent};
        border-color: ${ui.accent};
    }

    &:checked::after {
        transform: rotate(45deg) scale(1);
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${ui.accentRing};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

/* ---------- Toggle switch ---------- */

interface ToggleRowProps {
    title: string;
    description?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

export const ToggleRow: React.FC<ToggleRowProps> = ({ title, description, checked, onChange, disabled }) => (
    <ToggleLabel disabled={disabled}>
        <RowText>
            <RowTitle>{title}</RowTitle>
            {description && <RowDetail>{description}</RowDetail>}
        </RowText>
        <Switch
            type="checkbox"
            role="switch"
            aria-checked={checked}
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
        />
    </ToggleLabel>
);

const ToggleLabel = styled.label<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 44px;
    padding: 8px 16px;
    box-sizing: border-box;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
`;

const Switch = styled.input`
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    flex-shrink: 0;
    width: 38px;
    height: 22px;
    margin: 0;
    border-radius: 11px;
    background: rgba(120, 120, 128, 0.32);
    cursor: inherit;
    transition: background 0.2s ease;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #FFFFFF;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        transition: transform 0.2s ease;
    }

    &:checked {
        background: ${ui.success};
    }

    &:checked::after {
        transform: translateX(16px);
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${ui.accentRing};
    }

    &:disabled {
        opacity: 0.5;
    }
`;

/* ---------- Empty state ---------- */

export const EmptyGroup = styled(Group)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    text-align: center;
    font-size: 14px;
    color: ${ui.secondaryLabel};
`;

/* ---------- Activity log ---------- */

interface ActivityLogProps {
    title: string;
    lines: string[];
    isRunning?: boolean;
    onClear?: () => void;
}

const getLineTone = (line: string): 'success' | 'error' | 'warning' | 'default' => {
    const trimmed = line.trim();
    if (trimmed.includes('✗') || /^(fatal error|error|failed)/i.test(trimmed)) return 'error';
    if (trimmed.includes('⚠')) return 'warning';
    if (trimmed.includes('✓')) return 'success';
    return 'default';
};

const lineColors = {
    success: '#2E8B6A',
    error: ui.destructive,
    warning: '#B26A00',
    default: ui.label
};

export const ActivityLog: React.FC<ActivityLogProps> = ({ title, lines, isRunning, onClear }) => {
    if (lines.length === 0) return null;
    return (
        <GroupSection>
            <GroupTitle>{title}</GroupTitle>
            <Group>
                <Row>
                    <RowText>
                        <RowTitle>
                            {isRunning && <RunningDot />}
                            {isRunning ? 'In progress…' : 'Finished'}
                        </RowTitle>
                    </RowText>
                    {!isRunning && onClear && <ActionButton variant="plain" text="Clear" onClick={onClear} />}
                </Row>
                <LogBody>
                    {lines.map((line, index) => (
                        <LogLine key={index} tone={getLineTone(line)}>{line}</LogLine>
                    ))}
                </LogBody>
            </Group>
        </GroupSection>
    );
};

const RunningDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 8px;
    border-radius: 50%;
    background: ${ui.accent};
    animation: management-pulse 1.2s ease-in-out infinite;

    @keyframes management-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
    }
`;

const LogBody = styled.div`
    max-height: 280px;
    overflow-y: auto;
    padding: 10px 16px 12px;
    background: #FBFBFD;
    font-family: ${ui.monospace};
    font-size: 12px;
`;

const LogLine = styled.div<{ tone: keyof typeof lineColors }>`
    color: ${props => lineColors[props.tone]};
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
`;
